import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { createRandom } from '@/lib/motion'

// Mahonia confusa as a graphic symbol rather than an illustration: a monoline stem, straight
// pinnate leaves of lens-shaped leaflets, flower spikes strung with pale yellow to gold ✦, a few
// loose ✦ and + marks,
// and (on the branch) faint orbit rings behind the crown. The motif stands for Nia and MAHO.
// It is built in the browser as an image, so the page itself stays light.

export type MahoniaVariant = 'branch' | 'leaf' | 'flower'
export type MahoniaTone = 'day' | 'night'

interface MahoniaSprigProps {
    variant?: MahoniaVariant
    seed?: number
    tone?: MahoniaTone
    className?: string
}

interface Palette {
    line: string
    leaf: Array<string>
    flower: string
    flowerLight: string
    accent: string
    sparkle: string
    // Flower ✦ tones, palest (spike tip) to deepest gold (spike base).
    stars: Array<string>
}

type Random = () => number

const PALETTES: Record<MahoniaTone, Palette> = {
    day: {
        line: '#6a8a5e',
        leaf: ['rgba(226,236,214,0.82)', 'rgba(214,228,199,0.78)', 'rgba(236,242,228,0.86)'],
        flower: '#e2b33c',
        flowerLight: '#fbeab4',
        accent: '#c9a24a',
        sparkle: '#e2b33c',
        stars: ['#f5df98', '#efcf6f', '#e8c154', '#e2b33c', '#d4a32e']
    },
    night: {
        line: '#a9bfa9',
        leaf: ['rgba(169,191,169,0.16)', 'rgba(169,191,169,0.1)', 'rgba(169,191,169,0.2)'],
        flower: '#e2b33c',
        flowerLight: '#fff1bf',
        accent: '#e2b33c',
        sparkle: '#f3d27a',
        stars: ['#fff1bf', '#fae3a0', '#f3d27a', '#ebc155', '#e2b33c']
    }
}

const VIEWBOX: Record<MahoniaVariant, string> = {
    branch: '0 0 400 600',
    leaf: '0 0 420 240',
    flower: '0 0 300 320'
}

const ASPECT: Record<MahoniaVariant, string> = {
    branch: 'aspect-[2/3]',
    leaf: 'aspect-[7/4]',
    flower: 'aspect-[15/16]'
}

const SPARKLE = 'M12 0C12.9 7.4 16.6 11.1 24 12C16.6 12.9 12.9 16.6 12 24C11.1 16.6 7.4 12.9 0 12C7.4 11.1 11.1 7.4 12 0Z'

const r1 = (value: number): number => Math.round(value * 10) / 10

const stroke = (palette: Palette, width = 1.4): string =>
    `stroke="${palette.line}" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"`

const sparkle = (palette: Palette, x: number, y: number, size: number): string =>
    `<path d="${SPARKLE}" fill="${palette.sparkle}" transform="translate(${r1(x - size / 2)} ${r1(y - size / 2)}) scale(${r1(size / 24 * 100) / 100})"/>`

const plus = (palette: Palette, x: number, y: number, size: number): string =>
    `<path d="M${r1(x - size)} ${r1(y)}H${r1(x + size)}M${r1(x)} ${r1(y - size)}V${r1(y + size)}" ${stroke(palette, 1)} fill="none" stroke-opacity="0.7"/>`

const jitter = (rand: Random, amount: number): number => (rand() - 0.5) * amount

// A slender, lance-shaped leaflet (Mahonia confusa has narrow leaves) whose tip curls very slightly.
// `side` mirrors the curl so both rows of a leaf curl outwards.
const blade = (length: number): string => {
    const width = length * 0.085
    const curl = length * 0.05

    return `M0 0C${r1(length * 0.28)} ${r1(-width * 1.2)} ${r1(length * 0.7)} ${r1(-width * 0.9 - curl * 0.4)} ${r1(length)} ${r1(-curl)}C${r1(length * 0.68)} ${r1(width * 0.55 - curl * 0.5)} ${r1(length * 0.3)} ${r1(width)} 0 0Z`
}

const leaflet = (palette: Palette, x: number, y: number, angle: number, length: number, fill: string, side = 1): string =>
    `<g transform="translate(${r1(x)} ${r1(y)}) rotate(${r1(angle)}) scale(1 ${side})"><path d="${blade(length)}" fill="${fill}" ${stroke(palette, 1)}/><path d="M${r1(length * 0.08)} 0Q${r1(length * 0.5)} ${r1(-length * 0.015)} ${r1(length * 0.86)} ${r1(-length * 0.038)}" fill="none" ${stroke(palette, 0.7)} stroke-opacity="0.5"/></g>`

const WIND = 0.07

// A pinnate leaf on a softly arched rachis: horizontal leaves droop a little under their own
// weight and every leaf leans the same way, as if a summer wind were passing. Narrow leaflets
// sweep forward in pairs and taper to a terminal leaflet.
const leaf = (rand: Random, palette: Palette, x: number, y: number, angle: number, length: number, pairs: number): string => {
    const rad = (angle * Math.PI) / 180
    const dx = Math.cos(rad)
    const dy = Math.sin(rad)
    const ex = x + dx * length
    const ey = y + dy * length
    const droop = 0.16 * Math.abs(dx)
    const cx = x + dx * length * 0.5 + length * WIND
    const cy = y + dy * length * 0.5 + length * droop
    const fill = palette.leaf[Math.floor(rand() * palette.leaf.length)] ?? palette.leaf[0]

    const pointAt = (t: number): { px: number, py: number, heading: number } => {
        const u = 1 - t
        const px = u * u * x + 2 * u * t * cx + t * t * ex
        const py = u * u * y + 2 * u * t * cy + t * t * ey
        const tx = 2 * u * (cx - x) + 2 * t * (ex - cx)
        const ty = 2 * u * (cy - y) + 2 * t * (ey - cy)

        return { px, py, heading: (Math.atan2(ty, tx) * 180) / Math.PI }
    }

    let out = `<path d="M${r1(x)} ${r1(y)}Q${r1(cx)} ${r1(cy)} ${r1(ex)} ${r1(ey)}" fill="none" ${stroke(palette, 1.1)}/>`

    for (let index = 0; index < pairs; index++) {
        const t = 0.18 + (index / Math.max(1, pairs - 1)) * 0.68
        const { px, py, heading } = pointAt(t)
        const size = length * (0.37 - t * 0.12)

        for (const side of [-1, 1]) {
            out += leaflet(palette, px, py, heading + side * (40 + jitter(rand, 8)), size * (0.9 + rand() * 0.2), fill, side)
        }
    }

    const tip = pointAt(1)

    return out + leaflet(palette, ex, ey, tip.heading, length * 0.28, fill)
}

// One small ✦ centred on (x, y), turned by angle degrees.
const star = (x: number, y: number, size: number, angle: number, fill: string, opacity = 1): string =>
    `<path d="${SPARKLE}" fill="${fill}" fill-opacity="${opacity}" transform="translate(${r1(x)} ${r1(y)}) rotate(${r1(angle)}) scale(${Math.round(size / 24 * 1000) / 1000}) translate(-12 -12)"/>`

// A flower spike strung with ✦: each flower is a small star on a short stalk, alternating sides,
// deeper gold and larger at the base, paler and smaller towards the tip, each turned a little
// differently. One tiny loose star drifts past the tip.
const spike = (rand: Random, palette: Palette, x: number, y: number, angle: number, length: number): string => {
    const rad = (angle * Math.PI) / 180
    const dx = Math.cos(rad)
    const dy = Math.sin(rad)
    const tones = palette.stars
    const count = 9 + Math.floor(rand() * 3)

    let out = `<path d="M${r1(x)} ${r1(y)}L${r1(x + dx * length)} ${r1(y + dy * length)}" fill="none" ${stroke(palette, 1)}/>`

    for (let index = 0; index < count; index++) {
        const progress = index / (count - 1)
        const t = 0.28 + progress * 0.7 + jitter(rand, 0.03)
        const side = index % 2 === 0 ? 1 : -1
        const offset = (5.2 - progress * 2.6) * side + jitter(rand, 1.4)
        const bx = x + dx * length * t
        const by = y + dy * length * t
        const px = bx - dy * offset
        const py = by + dx * offset
        const size = (11 - progress * 5.6) * (0.78 + rand() * 0.44)
        const toneIndex = Math.min(tones.length - 1, Math.max(0, Math.round((1 - progress) * (tones.length - 1) + jitter(rand, 1.8))))
        const turn = jitter(rand, 56) + (rand() < 0.18 ? 45 : 0)

        out += `<path d="M${r1(bx)} ${r1(by)}L${r1(px)} ${r1(py)}" fill="none" ${stroke(palette, 0.7)} stroke-opacity="0.55"/>`
        out += star(px, py, size, turn, tones[toneIndex] ?? palette.flower, r1(0.82 + rand() * 0.18))
    }

    const reach = length * (1.08 + rand() * 0.06)

    out += star(x + dx * reach + jitter(rand, 8), y + dy * reach + jitter(rand, 8), 4 + rand() * 1.5, jitter(rand, 40), tones[0] ?? palette.flowerLight, 0.75)

    return out
}

const buildBranch = (rand: Random, palette: Palette): { back: string, front: string } => {
    const crownX = 204
    const crownY = 200 + jitter(rand, 20)
    const pairs = 5 + Math.floor(rand() * 2)

    let back = `<circle cx="${crownX}" cy="${r1(crownY)}" r="150" fill="none" stroke="${palette.accent}" stroke-opacity="0.4" stroke-width="1" stroke-dasharray="2 7" vector-effect="non-scaling-stroke"/>`

    back += `<circle cx="${crownX}" cy="${r1(crownY)}" r="104" fill="none" stroke="${palette.accent}" stroke-opacity="0.28" stroke-width="1" vector-effect="non-scaling-stroke"/>`
    back += plus(palette, crownX + 150, crownY, 6) + plus(palette, crownX - 150, crownY, 6) + plus(palette, crownX, crownY - 150, 6)
    back += sparkle(palette, 92, crownY - 96, 12) + sparkle(palette, 318, crownY - 40, 9) + sparkle(palette, 286, crownY + 150, 7)

    // A soft S-shaped stem leaning with the wind, with a few short grass blades at its foot.
    let front = `<path d="M200 600C203 486 ${r1(184 + jitter(rand, 8))} 344 ${crownX} ${r1(crownY)}" fill="none" ${stroke(palette, 1.5)}/>`

    front += `<path d="M196 600Q186 574 170 560M204 600Q214 578 232 570M190 600Q176 586 158 584" fill="none" ${stroke(palette, 0.9)} stroke-opacity="0.6"/>`

    front += leaf(rand, palette, 200, 470, -150 + jitter(rand, 10), 118, pairs)
    front += leaf(rand, palette, 200, 412, -30 + jitter(rand, 10), 116, pairs)
    front += leaf(rand, palette, 199, 336, -156 + jitter(rand, 8), 104, pairs - 1)
    front += leaf(rand, palette, 199, 288, -24 + jitter(rand, 8), 100, pairs - 1)

    for (const angle of [-166, -132, -48, -14]) {
        front += leaf(rand, palette, crownX, crownY, angle + jitter(rand, 8), 124 + jitter(rand, 16), pairs)
    }

    front += spike(rand, palette, crownX, crownY, -100, 116)
    front += spike(rand, palette, crownX, crownY, -90, 140)
    front += spike(rand, palette, crownX, crownY, -80, 112)

    return { back, front }
}

const buildLeaf = (rand: Random, palette: Palette): { back: string | null, front: string } => {
    const front = leaf(rand, palette, 20, 140, -12, 320, 6) + sparkle(palette, 380, 52, 12) + plus(palette, 52, 60, 6)

    return { back: null, front }
}

const buildFlower = (rand: Random, palette: Palette): { back: string | null, front: string } => {
    let front = `<path d="M150 320V230" fill="none" ${stroke(palette, 1.6)}/>`

    front += leaf(rand, palette, 150, 278, -158, 86, 3)
    front += leaf(rand, palette, 150, 262, -22, 82, 3)
    front += spike(rand, palette, 150, 230, -104, 92)
    front += spike(rand, palette, 150, 230, -90, 118)
    front += spike(rand, palette, 150, 230, -76, 90)
    front += sparkle(palette, 60, 112, 10) + plus(palette, 246, 132, 5)

    return { back: null, front }
}

const toSrc = (variant: MahoniaVariant, body: string): string =>
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="${VIEWBOX[variant]}">${body}</svg>`)}`

const cache = new Map<string, { back: string | null, front: string }>()

export const getMahoniaSrc = (variant: MahoniaVariant, seed: number, tone: MahoniaTone): { back: string | null, front: string } => {
    const key = `${variant}:${seed}:${tone}`
    const cached = cache.get(key)

    if (cached) return cached

    const rand = createRandom(seed * 7919 + variant.length * 104729)
    const palette = PALETTES[tone]
    const layers = variant === 'branch' ? buildBranch(rand, palette) : variant === 'leaf' ? buildLeaf(rand, palette) : buildFlower(rand, palette)
    const result = {
        back: layers.back ? toSrc(variant, layers.back) : null,
        front: toSrc(variant, layers.front)
    }

    cache.set(key, result)

    return result
}

const MahoniaSprig: FC<MahoniaSprigProps> = ({
    variant = 'branch',
    seed = 1,
    tone = 'day',
    className
}): ReactNode => {
    const [layers, setLayers] = useState<{ back: string | null, front: string } | null>(null)

    useEffect(() => {
        let cancelled = false

        const run = (): void => {
            if (!cancelled) setLayers(getMahoniaSrc(variant, seed, tone))
        }

        // Build the vector when the browser is idle so it never competes with the opening sequence.
        if (typeof window.requestIdleCallback === 'function') {
            const id = window.requestIdleCallback(run, { timeout: 900 })

            return () => {
                cancelled = true
                window.cancelIdleCallback(id)
            }
        }

        const id = window.setTimeout(run, 0)

        return () => {
            cancelled = true
            window.clearTimeout(id)
        }
    }, [variant, seed, tone])

    return (
        <span aria-hidden='true' className={cn('relative block', ASPECT[variant], className)}>
            {layers?.back && (
                <span className='absolute inset-0 origin-center motion-safe:animate-flutter'>
                    <img src={layers.back} alt='' draggable={false} className='block h-full w-full select-none object-contain' />
                </span>
            )}
            {layers && (
                <img src={layers.front} alt='' draggable={false} className='absolute inset-0 block h-full w-full select-none object-contain' />
            )}
        </span>
    )
}

export default MahoniaSprig
